import dynamic from 'next/dynamic';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { getAuth } from '@services/identity.service';
const NavHeader = dynamic(() => import('@molecules/NavHeader'), {
    ssr: false,
});
const Footer = dynamic(() => import('@molecules/Footer'), {
    ssr: false,
});
import AuthContext from 'context/auth';
import useScrollToTopOnRouteChange from '@ui/hooks/useScrollToTopOnRouteChange';
import BottomNavigation from '@molecules/BottomNavigation';
import bottomNavigationTexts from "@molecules/BottomNavigation/en.json";
import { useWindowSize } from '@uidotdev/usehooks';
import routes from '@helpers/routes';
import {
    hasDarkMode,
} from '@helpers/routes/theme';

const StaticLayout = ({ children, usePlainBackground = false }) => {
    useScrollToTopOnRouteChange();
    const router = useRouter();
    const { loginOrSignupModal, logout, investor, rmDetails, showRMDetails } = useContext(AuthContext);
    const [auth, setAuth] = useState();
    const [isBottomNavVisible, setIsBottomNavVisible] = useState(false);
    const showBottomNavigation = bottomNavigationTexts?.hideBottomNavOnFirstLevelPages.indexOf(router?.pathname) === -1;
    const { width } = useWindowSize();

    useEffect(() => {
        if (!auth) {
            const authObj = getAuth();
            if (authObj) {
                setAuth(authObj);
            }
        }
    }, [auth?.token]);

    // code for auto-logging out after 2 hrs of inactivity
    const debounceRef = useRef();
    const time = 1000 * 60 * 60 * 2; // 2 hrs

    debounceRef.current = (function debounce() {
        let timerId;

        return function () {
            if (timerId) {
                clearTimeout(timerId);
            }

            timerId = setTimeout(() => {
                if (getAuth()) {
                    logout();
                }
            }, time);
        };
    })();

    function tracker() {
        window.addEventListener('mousemove', debounceRef.current);
        window.addEventListener('scroll', debounceRef.current);
        window.addEventListener('keydown', debounceRef.current);
    }

    useEffect(() => {
        debounceRef.current();
        tracker();
    }, []);

    const bgColor =
        router.pathname.includes('/create-new-account') && '!bg-basicWhite';
    const height =
        !router.pathname.includes('/create-new-account') && 'min-h-screen';
    const showContactUsBg = useMemo(
        () => router.pathname.includes('/contact-us'),
        [router.pathname]
    );
    const isReferralPage = useMemo(
        () => router.pathname.includes('/referral-program'),
        [router.pathname]
    );
    const fullWidthPaths = [
        '/media',
        routes.bonds.base,
        routes.bonds.bondsIsin,
        '/webinars',
        '/calculators'
    ]

    const isFullWidth = fullWidthPaths.some(path => router.pathname.includes(path));
    const isDarkMode = hasDarkMode(router.pathname);

    return (
        <div
            className={`homepage-wrapper ${
                isBottomNavVisible ? 'pb-[68px] xl:pb-0' : 'pb-0'
            } ${!usePlainBackground ? 'bg-bgColor-50' : ''} ${bgColor}`}
        >
            <NavHeader
                hideHeaderOnScroll={true}
                translucent={false}
                homePage={true}
                isDarkMode={isDarkMode}
            />
            <div
                className={`relative overflow-clip ${height} text-left ${
                    showContactUsBg || isFullWidth ? '' : 'page-wrapper'    
                } ${isReferralPage ? '!max-w-none' : ''}`}
            >
                {children}
                {loginOrSignupModal()}
            </div>

            <Footer
                entityType={auth?.entityType}
            />

            {width && width < 1280 && showBottomNavigation ? <BottomNavigation investor={investor} visible={true} rmDetails={rmDetails} setIsBottomNavVisible={setIsBottomNavVisible} showRMDetails={showRMDetails} /> : null}
        </div>
    );
};

StaticLayout.propTypes = {
    children: PropTypes.node,
};

export default StaticLayout;
